import type { ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { useSupportOptional } from '@/lib/support-context';
import {
  BRAND,
  LEGAL,
  getLegalOperatorReference,
  getLegalPartyLabel,
} from '@maintenance-tracker/shared';
import { useBillingCopy, useLegalIdentityDisplay } from '@/lib/public-config';

export const LEGAL_LAST_UPDATED = LEGAL.effectiveDate;

function ContactNavLink() {
  const support = useSupportOptional();
  const pathname = useRouterState({ select: (st) => st.location.pathname });
  if (!support || pathname === '/contact') return <Link to="/contact" className="hover:text-brand">Contact</Link>;
  return (
    <button type="button" className="hover:text-brand" onClick={() => support.openSupport('footer_contact')}>
      Contact
    </button>
  );
}

export function LegalNav() {
  return (
    <nav className="mt-12 flex flex-wrap gap-x-4 gap-y-2 border-t border-border pt-8 text-[13px] text-text-secondary">
      <Link to="/terms" className="hover:text-brand">Terms</Link>
      <Link to="/privacy" className="hover:text-brand">Privacy</Link>
      <Link to="/refunds" className="hover:text-brand">Refunds</Link>
      <Link to="/cookies" className="hover:text-brand">Cookies</Link>
      <Link to="/acceptable-use" className="hover:text-brand">Acceptable use</Link>
      <Link to="/disclaimer" className="hover:text-brand">Disclaimer</Link>
      <Link to="/subprocessors" className="hover:text-brand">Subprocessors</Link>
      <Link to="/security" className="hover:text-brand">Security</Link>
      <Link to="/sms-terms" className="hover:text-brand">SMS terms</Link>
      <Link to="/dpa" className="hover:text-brand">DPA</Link>
      <Link to="/accessibility" className="hover:text-brand">Accessibility</Link>
      <Link to="/data-requests" className="hover:text-brand">Data requests</Link>
      <ContactNavLink />
    </nav>
  );
}

function ContactBlock() {
  const display = useLegalIdentityDisplay();

  if (display === 'email') {
    return (
      <p>
        Contact: <a href={`mailto:${BRAND.helloEmail}`}>{BRAND.helloEmail}</a>
      </p>
    );
  }

  return (
    <p>
      <strong>{LEGAL.operatorName}</strong> (trading as {LEGAL.tradeName})
      <br />
      Individual operator — not a registered company
      <br />
      {LEGAL.address}
      <br />
      General: <a href={`mailto:${BRAND.helloEmail}`}>{BRAND.helloEmail}</a>
      <br />
      Privacy: <a href={`mailto:${BRAND.privacyEmail}`}>{BRAND.privacyEmail}</a>
    </p>
  );
}

export function TermsContent() {
  const billing = useBillingCopy();
  const display = useLegalIdentityDisplay();
  return (
    <>
      <p className="lead">
        These Terms of Service (&quot;Terms&quot;) are a binding agreement between you and{' '}
        <strong>{getLegalPartyLabel(display)}</strong> (&quot;{LEGAL.tradeName},&quot; &quot;we,&quot; &quot;us,&quot; or
        &quot;our&quot;) governing access to and use of the {BRAND.name} software-as-a-service platform at{' '}
        <a href={`https://${BRAND.domain}`}>{BRAND.domain}</a> and related services (the &quot;Service&quot;).
        By creating an account, clicking &quot;I agree,&quot; or using the Service, you agree to these Terms and
        our <Link to="/privacy">Privacy Policy</Link>.
      </p>
      <p>
        If you use the Service on behalf of a company, you represent that you have authority to bind that entity,
        and &quot;you&quot; refers to that entity.
      </p>

      <h2>1. Eligibility</h2>
      <p>
        You must be at least {LEGAL.minimumAge} years old and able to form a binding contract. The Service is
        intended for businesses (e.g. HVAC, plumbing, electrical, and other service shops), not consumers acting
        outside a trade or profession.
      </p>

      <h2>2. The Service</h2>
      <p>
        {BRAND.name} helps service businesses track maintenance agreements, score renewal risk, automate customer
        communications, and related retention workflows. We are <strong>not</strong> a field service management
        system, payroll provider, tax advisor, legal counsel, payment processor, or payment facilitator. We do not
        process, hold, or transmit money on behalf of you or your customers. Optional URLs you add to reminder emails
        point to pages you control; any payment there is solely between you and your customer. We do not dispatch
        technicians, employ your staff, or guarantee renewal outcomes. Risk scores and forecasts are estimates — see
        our <Link to="/disclaimer">Disclaimer</Link>.
      </p>

      <h2>3. Account registration &amp; security</h2>
      <ul>
        <li>You must provide accurate registration information and keep it current.</li>
        <li>You are responsible for all activity under your account and for safeguarding credentials.</li>
        <li>Notify us promptly at <a href={`mailto:${BRAND.securityEmail}`}>{BRAND.securityEmail}</a> if you suspect unauthorized access.</li>
        <li>We may suspend or terminate accounts that violate these Terms or pose security or abuse risk.</li>
      </ul>

      <h2>4. {billing.termsSectionTitle}</h2>
      {billing.isMerchantOfRecord ? (
        <>
          <p>
            Paid plans are sold through <strong>{billing.morLegalName}</strong> (&quot;{billing.morShortName}&quot;),
            which acts as the <strong>Merchant of Record</strong> (&quot;MoR&quot;) for subscription purchases. This
            means:
          </p>
          <ul>
            <li>
              Your purchase contract for subscription charges is with {billing.morShortName}, not directly with us
              for card charges.
            </li>
            <li>
              {billing.morShortName} collects payment, issues receipts/invoices, and calculates, collects, and remits
              applicable sales tax, VAT, GST, or similar transaction taxes where required.
            </li>
            <li>
              Prices may be shown exclusive or inclusive of tax depending on your location; {billing.morShortName}{' '}
              determines tax at checkout.
            </li>
            <li>
              We provide the software; {billing.morShortName} handles billing operations subject to{' '}
              <a href={billing.buyerTermsUrl} target="_blank" rel="noopener noreferrer">
                {billing.morShortName}&apos;s buyer terms
              </a>
              .
            </li>
          </ul>
        </>
      ) : (
        <p>
          Paid plans are billed through {billing.morShortName}. Subscription charges, receipts, and payment method
          updates are handled through {billing.portalName} linked from Settings → Billing.
        </p>
      )}
      <h3>4.1 Free trial</h3>
      <p>
        New workspaces may receive a {LEGAL.trialDays}-day trial of eligible paid features. Unless you cancel before
        the trial ends, {billing.morShortName} will charge your selected plan at the end of the trial. Trial
        eligibility and duration may change; we will show applicable terms at signup or checkout.
      </p>
      <h3>4.2 Auto-renewal</h3>
      <p>
        Subscriptions renew automatically each billing period (monthly unless otherwise stated) until canceled. By
        subscribing, you authorize recurring charges through {billing.morShortName} until you cancel.
      </p>
      <h3>4.3 Plan changes, payment method &amp; cancellation</h3>
      <p>
        Manage your subscription, payment method, invoices, upgrades, downgrades, and cancellation through the{' '}
        {billing.portalName} linked from Settings → Billing in the app. Cancellation stops future renewals; access
        typically continues through the end of the current paid period unless {billing.morShortName}&apos;s terms state
        otherwise. See our <Link to="/refunds">Refund &amp; Cancellation Policy</Link>.
      </p>
      <h3>4.4 Failed payments</h3>
      <p>
        If payment fails, {billing.morShortName} may retry charges and/or downgrade or suspend access after a grace
        period. We are not responsible for bank fees or foreign exchange charges imposed by your payment provider.
      </p>

      <h2>5. Your data &amp; your customers&apos; data</h2>
      <ul>
        <li><strong>You own</strong> the business data you upload (customer contacts, agreements, notes, etc.).</li>
        <li>You grant us a worldwide, non-exclusive license to host, copy, process, transmit, and display that data
          solely to provide and improve the Service, comply with law, and prevent abuse.</li>
        <li>For your customers&apos; personal data, you are the <strong>data controller</strong> and we act as your{' '}
          <strong>processor</strong> under our <Link to="/dpa">Data Processing Addendum</Link>.</li>
        <li>You are solely responsible for having a lawful basis and any required consents to collect and use your
          customers&apos; information, including for email and SMS outreach via the Service.</li>
      </ul>

      <h2>6. Acceptable use</h2>
      <p>
        You must comply with our <Link to="/acceptable-use">Acceptable Use Policy</Link>,{' '}
        <Link to="/sms-terms">SMS &amp; Messaging Terms</Link>, and applicable anti-spam, telemarketing, and privacy
        laws (including CAN-SPAM, TCPA, GDPR, UK GDPR, and ePrivacy rules as applicable). We may investigate
        complaints and suspend sending or accounts for violations.
      </p>

      <h2>7. Third-party services</h2>
      <p>
        The Service integrates with third parties (e.g. {billing.integrationsLabel}, email and
        SMS providers, field-service tools). You may optionally configure URLs in the Service that link to renewal or
        invoice pages you operate elsewhere; those sites are not part of the Service and we do not process transactions
        on them. Your use of third-party services may be subject to their terms. We are not responsible for third-party
        services outside our reasonable control.
      </p>

      <h2>8. Intellectual property</h2>
      <p>
        We and our licensors own the Service, software, documentation, branding, and all related IP. Except for the
        limited right to use the Service during an active subscription or trial, no rights are granted. You may not
        reverse engineer, resell, or create derivative works of the Service except as permitted by law.
      </p>

      <h2>9. Confidentiality</h2>
      <p>
        Each party may receive non-public information from the other. The receiving party will use reasonable care
        to protect it and use it only for the relationship. This does not apply to information that is public,
        independently developed, or rightfully received from a third party without restriction.
      </p>

      <h2>10. Disclaimers</h2>
      <p>
        THE SERVICE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; TO THE MAXIMUM EXTENT PERMITTED BY
        LAW, WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR
        PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT UNINTERRUPTED, ERROR-FREE, OR COMPLETELY SECURE OPERATION.
      </p>

      <h2>11. Limitation of liability</h2>
      <p>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEITHER PARTY WILL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL,
        CONSEQUENTIAL, COVER, OR PUNITIVE DAMAGES, OR LOST PROFITS, REVENUE, DATA, OR GOODWILL. OUR AGGREGATE
        LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATED TO THE SERVICE IN ANY TWELVE-MONTH PERIOD WILL NOT EXCEED
        THE GREATER OF (A) AMOUNTS YOU PAID US FOR THE SERVICE IN THAT PERIOD OR (B) USD $100. SOME JURISDICTIONS
        DO NOT ALLOW CERTAIN LIMITATIONS; IN THOSE CASES OUR LIABILITY IS LIMITED TO THE FULLEST EXTENT PERMITTED.
      </p>

      <h2>12. Indemnification</h2>
      <p>
        You will defend, indemnify, and hold harmless {getLegalOperatorReference(display)} from claims, damages, and expenses
        (including reasonable attorneys&apos; fees) arising from (a) your data or customer communications, (b) your
        violation of these Terms or law, or (c) your dispute with your customers.
      </p>

      <h2>13. Term &amp; termination</h2>
      <p>
        You may stop using the Service and delete your account at any time in Settings. We may suspend or terminate
        access for material breach, non-payment (via {billing.morShortName}), or risk to the Service or others. Upon termination,
        export your data before deletion where the feature is available. Sections that by nature should survive
        (IP, disclaimers, liability limits, indemnity, governing law) survive termination.
      </p>

      <h2>14. Changes</h2>
      <p>
        We may update these Terms. Material changes will be notified by email or in-app notice at least 30 days
        before they take effect where required by law. Continued use after the effective date constitutes acceptance.
        If you disagree, cancel your subscription and stop using the Service.
      </p>

      <h2>15. Governing law &amp; disputes</h2>
      <p>
        These Terms are governed by {LEGAL.governingLaw}, without regard to conflict-of-law rules. Exclusive venue
        for disputes not subject to mandatory arbitration lies in {LEGAL.venue}. Nothing limits either party&apos;s
        right to seek injunctive relief for IP or unauthorized access.
      </p>
      <p>
        If you are a consumer in the European Union or United Kingdom, you may also have mandatory rights under
        local law that cannot be waived by contract.
      </p>

      <h2>16. Contact</h2>
      <ContactBlock />
    </>
  );
}

export function PrivacyContent() {
  const billing = useBillingCopy();
  const display = useLegalIdentityDisplay();
  return (
    <>
      <p className="lead">
        This Privacy Policy describes how <strong>{getLegalPartyLabel(display)}</strong> (&quot;{BRAND.name},&quot;
        &quot;we,&quot; &quot;us&quot;) collects, uses, shares, and protects personal information when you visit{' '}
        {BRAND.domain}, create an account, or use our services (the &quot;Service&quot;).
      </p>
      <p>
        <strong>Effective date:</strong> {LEGAL.effectiveDate}
      </p>

      <h2>1. Roles: controller vs processor</h2>
      <ul>
        <li>
          <strong>Data controller</strong> — for account holder data (your name, email, business details, billing
          status, usage of the app, and support communications).
        </li>
        <li>
          <strong>Data processor</strong> — for personal data about <em>your</em> customers that you upload or sync
          (contacts, agreement history, communication logs). You decide what to import and how to use it; we process
          it on your instructions per our <Link to="/dpa">DPA</Link>.
        </li>
      </ul>

      <h2>2. Information we collect</h2>
      <h3>2.1 Account &amp; profile</h3>
      <ul>
        <li>Name, email address, business name, timezone, password (stored as a secure hash).</li>
        <li>Workspace settings, team members you invite, and onboarding preferences.</li>
      </ul>
      <h3>2.2 Service &amp; usage data</h3>
      <ul>
        <li>Basic server and application logs for security, debugging, and abuse prevention.</li>
        <li>Device/browser type, IP address, approximate location derived from IP, and referral source.</li>
      </ul>
      <h3>2.3 Customer data you provide</h3>
      <ul>
        <li>Names, emails, phone numbers, addresses, companies, agreement terms, payment status signals, and notes.</li>
        <li>Content of emails/SMS you send through the Service and delivery events (opens, bounces, replies).</li>
      </ul>
      <h3>2.4 Billing</h3>
      <p>{billing.privacyProcessorLine}</p>
      <h3>2.5 Cookies</h3>
      <p>
        See our <Link to="/cookies">Cookie Policy</Link>. We use only strictly necessary session cookies — no
        analytics or advertising cookies at this time.
      </p>
      <h3>2.6 Support &amp; communications</h3>
      <p>Messages you send to us, survey responses, and call/meeting notes if you contact support.</p>

      <h2>3. How we use information</h2>
      <table>
        <thead>
          <tr><th>Purpose</th><th>Examples</th><th>Lawful basis (GDPR)</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Provide the Service</td>
            <td>Authentication, renewal tracking, reminders, dashboards, exports</td>
            <td>Contract (Art. 6(1)(b))</td>
          </tr>
          <tr>
            <td>Billing &amp; account management</td>
            <td>Trials, subscriptions via {billing.morShortName}, plan limits</td>
            <td>Contract; Legal obligation for tax records</td>
          </tr>
          <tr>
            <td>Security &amp; fraud prevention</td>
            <td>Session validation, abuse detection, audit logs</td>
            <td>Legitimate interests (Art. 6(1)(f))</td>
          </tr>
          <tr>
            <td>Service reliability</td>
            <td>Server logs, debugging, incident investigation</td>
            <td>Legitimate interests (Art. 6(1)(f))</td>
          </tr>
          <tr>
            <td>Legal compliance</td>
            <td>Respond to lawful requests, enforce Terms</td>
            <td>Legal obligation (Art. 6(1)(c))</td>
          </tr>
          <tr>
            <td>Marketing to you (account holder)</td>
            <td>Product updates, tips — not your end customers</td>
            <td>Legitimate interests or consent where required</td>
          </tr>
        </tbody>
      </table>

      <h2>4. How we share information</h2>
      <p>We do <strong>not</strong> sell personal information. We share data only:</p>
      <ul>
        <li>With <Link to="/subprocessors">subprocessors</Link> under contract who assist in hosting, email, SMS, and billing.</li>
        <li>
          With {billing.morShortName} for subscription processing
          {billing.isMerchantOfRecord ? ' (they act as independent controller for checkout data)' : ''}.
        </li>
        <li>When you direct us to (e.g. sending email/SMS to your customers, including optional URLs to renewal pages you configure).</li>
        <li>For legal reasons, mergers, or to protect rights and safety.</li>
        <li>With your consent.</li>
      </ul>

      <h2>5. International transfers</h2>
      <p>
        We are based in {LEGAL.country} and use infrastructure primarily in the United States (Cloudflare) and other
        countries where our subprocessors operate. For transfers from the EEA, UK, or Switzerland we rely on
        appropriate safeguards such as the EU Standard Contractual Clauses (2021/914), UK IDTA/Addendum where
        applicable, and supplementary measures as needed. Contact{' '}
        <a href={`mailto:${BRAND.privacyEmail}`}>{BRAND.privacyEmail}</a> for a copy of relevant transfer mechanisms.
      </p>

      <h2>6. Retention</h2>
      <ul>
        <li><strong>Account data:</strong> retained while your account is active and up to 90 days after deletion for backups and legal holds, unless longer retention is required by law.</li>
        <li><strong>Customer data you upload:</strong> deleted or anonymized within 90 days of workspace deletion unless you export it first.</li>
        <li><strong>Billing records:</strong> retained as required for tax and accounting (typically 7 years via {billing.morShortName}/our records).</li>
        <li><strong>Logs &amp; security:</strong> typically 30–90 days unless needed for incident investigation.</li>
      </ul>

      <h2>7. Security</h2>
      <p>
        We implement administrative, technical, and organizational measures described in our{' '}
        <Link to="/security">Security Overview</Link>. No method of transmission or storage is 100% secure.
      </p>

      <h2 id="your-rights">8. Your privacy rights</h2>
      <p>Depending on your location, you may have the right to:</p>
      <ul>
        <li>Access, correct, or delete your personal information.</li>
        <li>Port your data (export in Settings or via <Link to="/data-requests">data request</Link>).</li>
        <li>Object to or restrict certain processing.</li>
        <li>Withdraw consent where processing is consent-based.</li>
        <li>Lodge a complaint with a supervisory authority (EU/UK users).</li>
      </ul>
      <p>
        <strong>Self-service:</strong> Export workspace data in Settings → Data export. Delete your account in
        Settings → Danger zone.
      </p>
      <p>
        <strong>Contact us:</strong> Email{' '}
        <a href={`mailto:${BRAND.privacyEmail}`}>{BRAND.privacyEmail}</a>. We respond within{' '}
        {LEGAL.dsarResponseDays} days where required by law. We may verify your identity before fulfilling requests.
      </p>
      <p>
        If you are an end customer of one of our business users, contact that business first — they control your data.
        We will assist them as processor.
      </p>

      <h2>9. United States privacy notices</h2>
      <h3>9.1 California (CCPA/CPRA)</h3>
      <p>
        We do not sell or share personal information for cross-context behavioral advertising as defined by California
        law. California residents may request access, deletion, and correction. We do not discriminate against you
        for exercising rights. Authorized agents may submit requests with proof of authority.
      </p>
      <h3>9.2 Other US states</h3>
      <p>
        Residents of Colorado, Connecticut, Virginia, Utah, and other states with comprehensive privacy laws may
        have similar rights. Contact <a href={`mailto:${BRAND.privacyEmail}`}>{BRAND.privacyEmail}</a>.
      </p>

      <h2>10. Children</h2>
      <p>
        The Service is not directed to individuals under {LEGAL.minimumAge}. We do not knowingly collect children&apos;s
        data. Contact us to request deletion if you believe we have collected such data.
      </p>

      <h2>11. Automated decision-making</h2>
      <p>
        Risk scores and lapse predictions are statistical estimates to help you prioritize outreach. They do not
        produce legal or similarly significant effects on individuals without human review by you. See{' '}
        <Link to="/disclaimer">Disclaimer</Link>.
      </p>

      <h2>12. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy. Material changes will be posted here with a new effective date and,
        where required, notified by email or in-app.
      </p>

      <h2>13. Contact</h2>
      <ContactBlock />
    </>
  );
}

export function RefundsContent() {
  const billing = useBillingCopy();
  return (
    <>
      <p className="lead">
        This Refund &amp; Cancellation Policy explains how subscriptions for {BRAND.name} work when purchased
        through {billing.morShortName}
        {billing.isMerchantOfRecord ? ' as Merchant of Record' : ''}. It supplements our{' '}
        <Link to="/terms">Terms of Service</Link>.
      </p>

      <h2>1. Merchant of Record</h2>
      {billing.isMerchantOfRecord ? (
        <p>
          <strong>{billing.morLegalName}</strong> (&quot;{billing.morShortName}&quot;) is the seller of record for
          your subscription. Charges appear on your statement as {billing.statementDescriptor}. Tax invoices and
          receipts are issued by {billing.morShortName}. Payment disputes may be handled under{' '}
          {billing.morShortName}&apos;s buyer terms in addition to this policy.
        </p>
      ) : (
        <p>
          Your subscription is billed through {billing.morShortName}. Receipts and payment method updates are managed
          in {billing.portalName}.
        </p>
      )}

      <h2>2. Free trial</h2>
      <p>
        Eligible new accounts receive a {LEGAL.trialDays}-day trial with access to trial features as shown at signup.
        <strong> Cancel before the trial ends</strong> to avoid being charged. If you do not cancel, {billing.morShortName} will
        automatically charge the plan and billing interval you selected at trial signup.
      </p>

      <h2>3. Subscription billing</h2>
      <ul>
        <li>Plans bill in advance on a recurring basis (monthly unless otherwise stated at checkout).</li>
        <li>Prices may change on renewal with advance notice; continued use after a price change constitutes acceptance.</li>
        <li>Applicable taxes are calculated and collected by {billing.morShortName} at checkout based on your location.</li>
      </ul>

      <h2>4. How to cancel</h2>
      <ol>
        <li>Sign in to {BRAND.name}.</li>
        <li>Go to <strong>Settings → Billing</strong>.</li>
        <li>Open the <strong>{billing.portalName}</strong> (Manage subscription).</li>
        <li>Follow {billing.morShortName}&apos;s steps to cancel renewal.</li>
      </ol>
      <p>
        Cancellation stops future charges. You typically retain access through the end of the current paid billing
        period. We do not provide refunds for partial unused periods except where required by law or {billing.morShortName} policy.
      </p>

      <h2>5. Refunds</h2>
      <p>Refund eligibility depends on the circumstances:</p>
      <ul>
        <li>
          <strong>Statutory rights:</strong> If you are in the EU, UK, or another jurisdiction with mandatory
          withdrawal or cooling-off rights, you may have a right to cancel within the period prescribed by local law.
          Digital services may be subject to exceptions once performance begins with your consent.
        </li>
        <li>
          <strong>Duplicate or erroneous charges:</strong> Contact us within 14 days at{' '}
          <a href={`mailto:${BRAND.helloEmail}`}>{BRAND.helloEmail}</a> with your {billing.receiptLabel}.
        </li>
        <li>
          <strong>Service issues:</strong> If the Service was unavailable for a material period due to our fault,
          contact us — we may offer account credit or coordinate a pro-rata refund with {billing.morShortName} at our discretion.
        </li>
        <li>
          <strong>General:</strong> Except as above or as required by law, fees are non-refundable once a billing
          period has started.
        </li>
      </ul>
      <p>
        {billing.morShortName} may process approved refunds to your original payment method. Processing times vary by bank (typically
        5–10 business days).
      </p>

      <h2>6. Downgrades &amp; upgrades</h2>
      <p>
        Plan changes are handled in the {billing.portalName}. Upgrades may take effect immediately with prorated charges.
        Downgrades typically apply at the next renewal unless {billing.morShortName} states otherwise at checkout.
      </p>

      <h2>7. Chargebacks</h2>
      <p>
        If you dispute a charge with your bank without contacting us first, we may suspend your account pending
        resolution. Please email <a href={`mailto:${BRAND.helloEmail}`}>{BRAND.helloEmail}</a> so we can help.
      </p>

      <h2>8. Contact</h2>
      <ContactBlock />
    </>
  );
}

export function CookiesContent() {
  const billing = useBillingCopy();
  return (
    <>
      <p className="lead">
        This Cookie Policy explains how {BRAND.name} uses cookies on {BRAND.domain}. For our MVP we use{' '}
        <strong>only strictly necessary cookies</strong> required to run the service. We do not use analytics,
        advertising, or other optional tracking cookies at this time.
      </p>

      <h2>1. What are cookies?</h2>
      <p>
        Cookies are small text files stored on your device. We use them only where needed for authentication and
        security.
      </p>

      <h2>2. Cookies we use</h2>
      <table>
        <thead>
          <tr><th>Name</th><th>Purpose</th><th>Duration</th><th>Provider</th></tr>
        </thead>
        <tbody>
          <tr>
            <td><code>mt_session</code></td>
            <td>Keeps you signed in securely</td>
            <td>Session or up to 30 days</td>
            <td>{BRAND.name}</td>
          </tr>
        </tbody>
      </table>
      <p>
        Because these cookies are strictly necessary to provide the Service, we do not ask for separate consent
        before setting them. Blocking them in your browser will prevent you from staying signed in.
      </p>

      <h2>3. Third-party cookies</h2>
      <p>
        {billing.cookieEmbedLine}
      </p>

      <h2>4. How to manage cookies</h2>
      <p>
        You can block or delete cookies in your browser settings. If we introduce optional cookies in the future
        (such as analytics), we will update this policy and request consent where required before using them.
      </p>

      <h2>5. Legal basis</h2>
      <p>
        Strictly necessary cookies are used based on contractual necessity and our legitimate interest in operating
        a secure, functional service.
      </p>

      <h2>6. Contact</h2>
      <p>
        Questions: <a href={`mailto:${BRAND.privacyEmail}`}>{BRAND.privacyEmail}</a>
      </p>
    </>
  );
}

export function AcceptableUseContent() {
  return (
    <>
      <p className="lead">
        This Acceptable Use Policy (&quot;AUP&quot;) applies to all use of {BRAND.name}. Violations may result in
        suspension or termination without refund.
      </p>

      <h2>1. Prohibited conduct</h2>
      <p>You may not use the Service to:</p>
      <ul>
        <li>Send spam, bulk unsolicited messages, or communications without valid consent and opt-out mechanisms.</li>
        <li>Violate CAN-SPAM, TCPA, GDPR, UK PECR, CASL, or other anti-spam or telemarketing laws.</li>
        <li>Harass, threaten, defame, or discriminate against any person.</li>
        <li>Transmit malware, phishing, or fraudulent content.</li>
        <li>Upload content that infringes intellectual property or privacy rights.</li>
        <li>Collect or process special-category personal data (health, biometric, etc.) without lawful basis and safeguards.</li>
        <li>Probe, scan, or test the vulnerability of our systems without written permission.</li>
        <li>Circumvent rate limits, authentication, or plan restrictions.</li>
        <li>Resell, sublicense, or white-label the Service without written agreement.</li>
        <li>Use the Service for illegal activity or to facilitate harm.</li>
      </ul>

      <h2>2. Messaging requirements</h2>
      <ul>
        <li>Identify your business clearly in emails and SMS.</li>
        <li>Include a working unsubscribe or STOP mechanism for marketing messages.</li>
        <li>Honor opt-outs promptly (we process STOP replies for SMS automatically where configured).</li>
        <li>Maintain records of consent for your customers.</li>
      </ul>

      <h2>3. Content standards</h2>
      <p>
        You are responsible for templates, campaigns, and data you upload. We do not routinely monitor customer
        content but may review material when investigating abuse reports or legal obligations.
      </p>

      <h2>4. Enforcement</h2>
      <p>
        We may warn, throttle, suspend sending, or terminate accounts. We may report illegal activity to authorities.
        If your account is suspended, contact <a href={`mailto:${BRAND.helloEmail}`}>{BRAND.helloEmail}</a>.
      </p>

      <h2>5. Reporting abuse</h2>
      <p>
        Report abuse or security issues at <a href={`mailto:${BRAND.securityEmail}`}>{BRAND.securityEmail}</a>.
      </p>
    </>
  );
}

export function DisclaimerContent() {
  return (
    <>
      <p className="lead">
        {BRAND.name} provides software tools to help service businesses manage maintenance agreement renewals.
        This Disclaimer clarifies the limits of what we provide.
      </p>

      <h2>1. Estimates, not guarantees</h2>
      <p>
        Renewal risk scores, lapse-risk percentages, revenue-at-risk figures, churn predictions, and similar outputs
        are <strong>statistical estimates for guidance only</strong>. They are based on data you provide and
        configurable rules — they are not guarantees of renewal, payment, or business outcomes.
      </p>

      <h2>2. Your decisions</h2>
      <p>
        You retain full responsibility for customer relationships, pricing, service quality, and communications. The
        Service does not replace professional judgment, legal compliance review, or financial planning.
      </p>

      <h2>3. Not professional advice</h2>
      <p>
        Nothing in the Service constitutes legal, tax, accounting, or financial advice. Consult qualified professionals
        for matters specific to your business.
      </p>

      <h2>4. Third-party data</h2>
      <p>
        Integrations and imports may contain incomplete or outdated information. Verify critical data before relying on it.
      </p>

      <h2>5. Availability</h2>
      <p>
        We strive for high availability but do not guarantee uninterrupted access. Planned maintenance and factors
        outside our control (internet outages, third-party failures) may affect the Service.
      </p>
    </>
  );
}

export function SubprocessorsContent() {
  const billing = useBillingCopy();
  const billingRow =
    billing.provider === 'dodo'
      ? {
          name: 'Dodo Payments',
          purpose: 'Subscription billing, tax, invoicing (Merchant of Record)',
          location: 'Singapore / global',
        }
      : billing.provider === 'stripe'
        ? {
            name: 'Stripe, Inc.',
            purpose: 'Subscription billing',
            location: 'United States / global',
          }
        : {
            name: 'Paddle.com Market Limited',
            purpose: 'Subscription billing, tax, invoicing (Merchant of Record)',
            location: 'United Kingdom / global',
          };

  return (
    <>
      <p className="lead">
        We use the following subprocessors to operate {BRAND.name}. We enter into data protection agreements with
        each where they process personal data on our behalf. We will update this list before engaging a new
        subprocessor that processes personal data.
      </p>
      <p><strong>Last updated:</strong> {LEGAL.effectiveDate}</p>

      <table>
        <thead>
          <tr><th>Provider</th><th>Purpose</th><th>Data processed</th><th>Location</th></tr>
        </thead>
        <tbody>
          <tr>
            <td>Cloudflare, Inc.</td>
            <td>Hosting, CDN, D1 database, KV, R2 storage, queues, DDoS protection</td>
            <td>All service data in transit and at rest</td>
            <td>United States / global edge</td>
          </tr>
          <tr>
            <td>{billingRow.name}</td>
            <td>{billingRow.purpose}</td>
            <td>Account email, billing address, payment metadata</td>
            <td>{billingRow.location}</td>
          </tr>
          <tr>
            <td>Resend, Inc.</td>
            <td>Transactional email delivery</td>
            <td>Email addresses, message content, delivery events</td>
            <td>United States</td>
          </tr>
          <tr>
            <td>Twilio, Inc.</td>
            <td>SMS messaging (when enabled)</td>
            <td>Phone numbers, message content, delivery status</td>
            <td>United States</td>
          </tr>
        </tbody>
      </table>

      <h2>Changes</h2>
      <p>
        We will post updates on this page. For material new subprocessors affecting processor data, we will provide
        notice per our <Link to="/dpa">DPA</Link> and applicable law.
      </p>
      <p>
        Enterprise customers may object to a new subprocessor on reasonable grounds by contacting{' '}
        <a href={`mailto:${BRAND.privacyEmail}`}>{BRAND.privacyEmail}</a>.
      </p>
    </>
  );
}

export function SecurityContent() {
  const billing = useBillingCopy();
  return (
    <>
      <p className="lead">
        We take security seriously. This overview summarizes technical and organizational measures for {BRAND.name}.
        It is not an exhaustive audit report.
      </p>

      <h2>1. Infrastructure</h2>
      <ul>
        <li>Hosted on Cloudflare Workers with D1 (SQL), KV, R2, and Queues.</li>
        <li>TLS 1.2+ encryption for data in transit.</li>
        <li>Workspace-scoped data isolation — users access only their tenant data.</li>
      </ul>

      <h2>2. Application security</h2>
      <ul>
        <li>Passwords hashed with industry-standard algorithms (never stored in plain text).</li>
        <li>Session cookies with HttpOnly and Secure flags in production.</li>
        <li>Integration OAuth tokens encrypted at rest.</li>
        <li>Role-based access within workspaces.</li>
        <li>Webhook signature verification for billing and integration events.</li>
      </ul>

      <h2>3. Operations</h2>
      <ul>
        <li>Least-privilege access for production systems.</li>
        <li>Automated dependency updates and security patching.</li>
        <li>Backups and disaster recovery via Cloudflare infrastructure.</li>
      </ul>

      <h2>4. Incident response</h2>
      <p>
        If we become aware of a personal data breach affecting your workspace, we will notify you without undue delay
        and provide information required under GDPR and applicable law. Report suspected vulnerabilities to{' '}
        <a href={`mailto:${BRAND.securityEmail}`}>{BRAND.securityEmail}</a> — please include steps to reproduce.
        Do not access data that is not yours.
      </p>

      <h2>5. Your responsibilities</h2>
      <ul>
        <li>Use strong, unique passwords and enable team access controls.</li>
        <li>Review integration permissions you grant to third-party tools.</li>
        <li>Export backups of critical data periodically.</li>
      </ul>

      <h2>6. Certifications</h2>
      <p>
        We rely on subprocessors with SOC 2 / ISO certifications where applicable (e.g. Cloudflare, {billing.subprocessorsName}). We do not
        currently hold our own SOC 2 report; contact us for security questionnaires.
      </p>
    </>
  );
}

export function SmsTermsContent() {
  return (
    <>
      <p className="lead">
        These SMS &amp; Messaging Terms apply when you enable text messaging features on eligible {BRAND.name} plans.
        They supplement our <Link to="/terms">Terms</Link> and <Link to="/acceptable-use">Acceptable Use Policy</Link>.
      </p>

      <h2>1. Your responsibilities as sender</h2>
      <p>
        <strong>You</strong> are the sender of record to your customers. You must:
      </p>
      <ul>
        <li>Obtain prior express consent (and express written consent for marketing under TCPA where required) before texting.</li>
        <li>Disclose message frequency and that consent is not a condition of purchase where applicable.</li>
        <li>Maintain consent records and honor opt-outs immediately.</li>
        <li>Comply with CTIA messaging principles, carrier A2P 10DLC registration requirements, and local laws.</li>
      </ul>

      <h2>2. Message content &amp; frequency</h2>
      <ul>
        <li>Messages may include renewal reminders, optional renewal page URLs, and campaign steps you configure.</li>
        <li>Frequency varies based on your settings and customer status.</li>
        <li>Messages are sent during reasonable hours based on your workspace timezone settings.</li>
      </ul>

      <h2>3. Standard disclosures</h2>
      <p>Include in your signup flows and message footers where appropriate:</p>
      <ul>
        <li>&quot;Msg &amp; data rates may apply.&quot;</li>
        <li>&quot;Reply STOP to unsubscribe&quot; / &quot;Reply HELP for help.&quot;</li>
        <li>Your business name and contact information.</li>
      </ul>

      <h2>4. Opt-out handling</h2>
      <p>
        Recipients may reply <strong>STOP</strong> to opt out of further texts. We process STOP keywords automatically
        where configured and suppress future sends to that number for your workspace. Recipients may reply{' '}
        <strong>HELP</strong> for assistance — configure a help response with your support contact.
      </p>

      <h2>5. Our role</h2>
      <p>
        We provide the technical platform to send messages you authorize. We are not responsible for your compliance
        failures, content, or consent practices. We may suspend messaging if carriers, regulators, or abuse reports
        require it.
      </p>

      <h2>6. Prohibited content</h2>
      <p>No SHAFT content (sex, hate, alcohol, firearms, tobacco), illegal offers, phishing, or high-risk financial schemes.</p>

      <h2>7. Contact</h2>
      <p>
        Messaging support: <a href={`mailto:${BRAND.helloEmail}`}>{BRAND.helloEmail}</a>
      </p>
    </>
  );
}

export function DpaContent() {
  const display = useLegalIdentityDisplay();
  return (
    <>
      <p className="lead">
        This Data Processing Addendum (&quot;DPA&quot;) forms part of the agreement between you (&quot;Controller&quot;)
        and <strong>{getLegalPartyLabel(display)}</strong> (&quot;Processor&quot;) when you use {BRAND.name} to process personal
        data about your customers. It satisfies GDPR Article 28 and UK GDPR equivalent requirements.
      </p>

      <h2>1. Definitions</h2>
      <p>
        &quot;Personal data,&quot; &quot;processing,&quot; &quot;controller,&quot; &quot;processor,&quot; and
        &quot;data subject&quot; have the meanings in applicable data protection law. &quot;Applicable law&quot;
        includes the EU GDPR, UK GDPR, and similar regulations.
      </p>

      <h2>2. Scope &amp; instructions</h2>
      <p>
        We process personal data only on documented instructions from you — primarily to provide renewal tracking,
        risk scoring, communications, integrations, exports, and support. You instruct us by using the Service and
        configuring features. We will inform you if we believe an instruction infringes applicable law.
      </p>

      <h2>3. Categories of data &amp; subjects</h2>
      <ul>
        <li><strong>Subjects:</strong> Your customers, prospects, and contacts.</li>
        <li><strong>Categories:</strong> Contact details, agreement terms, service history, communication preferences, payment status signals, and message content you send.</li>
        <li><strong>Special categories:</strong> Not intended — do not upload unless you have lawful basis and safeguards.</li>
      </ul>

      <h2>4. Confidentiality</h2>
      <p>
        Personnel authorized to process personal data are bound by confidentiality obligations.
      </p>

      <h2>5. Security</h2>
      <p>
        We implement measures described in our <Link to="/security">Security Overview</Link> and maintain appropriate
        technical and organizational security.
      </p>

      <h2>6. Subprocessors</h2>
      <p>
        You authorize our use of subprocessors listed at <Link to="/subprocessors">Subprocessors</Link>. We impose
        data protection obligations on subprocessors substantially similar to this DPA. We remain liable for
        subprocessors&apos; performance of their obligations.
      </p>

      <h2>7. Data subject rights</h2>
      <p>
        We assist you in responding to data subject requests to the extent technically feasible, considering the
        nature of processing. Direct requests received by us will be forwarded to you unless prohibited by law.
      </p>

      <h2>8. Personal data breaches</h2>
      <p>
        We notify you without undue delay after becoming aware of a personal data breach affecting your workspace data
        and provide information reasonably required for your regulatory notifications.
      </p>

      <h2>9. Data protection impact assessments</h2>
      <p>We provide reasonable assistance with DPIAs and prior consultations where required and proportionate.</p>

      <h2>10. Deletion &amp; return</h2>
      <p>
        On termination or your written request, we delete or return personal data per our retention schedule unless
        storage is required by law. Export tools are available in Settings before deletion.
      </p>

      <h2>11. International transfers</h2>
      <p>
        Transfers outside the EEA/UK use appropriate safeguards (Standard Contractual Clauses and supplementary
        measures). Details in our <Link to="/privacy">Privacy Policy</Link>.
      </p>

      <h2>12. Audits</h2>
      <p>
        Upon reasonable request, we provide information necessary to demonstrate compliance and allow audits no more
        than once annually with 30 days&apos; notice, subject to confidentiality and security restrictions.
      </p>

      <h2>13. Order of precedence</h2>
      <p>
        If this DPA conflicts with the Terms regarding processor obligations, this DPA controls. If mandatory law
        imposes stricter requirements, those apply.
      </p>

      <h2>14. Signed copies</h2>
      <p>
        This online DPA is binding when you use the Service. For a countersigned PDF, email{' '}
        <a href={`mailto:${BRAND.helloEmail}`}>{BRAND.helloEmail}</a>.
      </p>
    </>
  );
}

export function AccessibilityContent() {
  const billing = useBillingCopy();
  return (
    <>
      <p className="lead">
        {BRAND.name} is committed to making our website and application accessible to people with disabilities. We aim
        to conform to WCAG 2.1 Level AA over time.
      </p>

      <h2>1. Measures we take</h2>
      <ul>
        <li>Semantic HTML and ARIA labels on interactive controls where needed.</li>
        <li>Keyboard navigability for core workflows.</li>
        <li>Sufficient color contrast for text and interactive elements.</li>
        <li>Responsive layouts that work with zoom up to 200%.</li>
        <li>Visible focus indicators for keyboard users.</li>
      </ul>

      <h2>2. Known limitations</h2>
      <p>
        Some third-party embeds (e.g. {billing.checkoutEmbedLine}, payment providers linked in your emails) are controlled by those providers and may not
        fully meet our accessibility targets. Data tables and complex charts may require ongoing improvement for
        screen reader users.
      </p>

      <h2>3. Feedback &amp; assistance</h2>
      <p>
        If you encounter accessibility barriers, contact{' '}
        <a href={`mailto:${BRAND.helloEmail}`}>{BRAND.helloEmail}</a> with the page URL and description. We will try
        to provide the information in an alternative format and fix issues in future releases.
      </p>

      <h2>4. Compatibility</h2>
      <p>
        We test with current versions of major browsers (Chrome, Firefox, Safari, Edge) and assistive technologies
        where feasible.
      </p>
    </>
  );
}

export function DataRequestsContent() {
  const mailto = `mailto:${BRAND.privacyEmail}?subject=${encodeURIComponent(`Data request — ${BRAND.name}`)}&body=${encodeURIComponent(
    'Please complete:\n\n1. Request type (access / correction / deletion / portability / objection):\n2. Email associated with your account:\n3. Additional details:\n\nWe may verify your identity before processing.',
  )}`;

  return (
    <>
      <p className="lead">
        Use this page to exercise your privacy rights under GDPR, UK GDPR, CCPA, and similar laws. We respond within{' '}
        {LEGAL.dsarResponseDays} days where required.
      </p>

      <h2>1. Self-service (fastest)</h2>
      <ul>
        <li><strong>Export:</strong> Settings → Data export — download your workspace contacts and agreements.</li>
        <li><strong>Delete account:</strong> Settings → Danger zone — permanently deletes your workspace data.</li>
      </ul>

      <h2>2. Email request</h2>
      <p>
        For requests you cannot complete in-app, email{' '}
        <a href={`mailto:${BRAND.privacyEmail}`}>{BRAND.privacyEmail}</a> from your account email. Include:
      </p>
      <ul>
        <li>Your full name and business name on the account.</li>
        <li>Request type: access, correction, deletion, portability, restriction, or objection.</li>
        <li>Enough detail for us to locate your data.</li>
      </ul>
      <p>
        <a href={mailto} className="inline-flex items-center rounded-md bg-brand px-4 py-2 text-sm font-medium text-white hover:opacity-90">
          Open email template
        </a>
      </p>

      <h2>3. End customers of our users</h2>
      <p>
        If you are a customer of a shop using {BRAND.name} (e.g. you received a renewal email from them), contact that
        business directly — they control your data. We will assist them as processor.
      </p>

      <h2>4. Authorized agents (California)</h2>
      <p>
        Agents may submit requests with signed authorization and proof of registration where applicable.
      </p>

      <h2>5. Appeals</h2>
      <p>
        If we deny a request, we will explain why and how to appeal where required by law.
      </p>

      <h2>6. Contact</h2>
      <ContactBlock />
    </>
  );
}

/** Optional table of contents for long pages */
export function LegalToc({ items }: { items: { id: string; label: string }[] }) {
  return (
    <nav className="not-prose mb-8 rounded-lg border border-border bg-bg-muted p-4 text-[13px]">
      <p className="mb-2 font-semibold text-text-primary">On this page</p>
      <ul className="space-y-1 text-text-secondary">
        {items.map((item) => (
          <li key={item.id}>
            <a href={`#${item.id}`} className="hover:text-brand">
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  );
}

export function LegalProse({ children }: { children: ReactNode }) {
  return <div className="legal-prose">{children}</div>;
}
